import { useState } from "react";
import { Mail } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { login } from "./http/LoginAPI";
import { clearAuthStorage, storeAuthSession } from "../../../common/helpers";
import abgLogo from "../../../assets/imgs/blue-logo.svg";
import OutterLeftPanel from "../../../components/OutterLeftPanel";
import AuthInput, {
  LockNoKeyholeIcon,
} from "../../../components/authentication-input/AuthInput";
import { isSsoConfigured, startSsoLogin } from "../../../common/ssoHelper";

export function Login() {
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [rememberMe, setRememberMe] = useState(true);
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  const emailValue = userName.trim();
  const passwordValue = password.trim();
  const emailFormatValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
  const isEmailValid = emailValue.length > 0 && emailFormatValid;
  const isPasswordValid = passwordValue.length > 0;
  const ssoEnabled = isSsoConfigured();

  const handleLogin = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!validateCredentials()) return;
    setLoading(true);

    try {
      const response = await login({ userName, password });
      if (response) {
        clearAuthStorage();
        storeAuthSession(response.content);
        toast.success("Logged in successfully!");
        navigate("/");
      }
    } catch (error: any) {
      const errorMessage =
        error.message ||
        "Login failed. Please check your credentials and try again.";
      toast.error(errorMessage);
    }

    setLoading(false);
  };

  const validateCredentials = () => {
    return userName.trim().length > 0 && password.trim().length > 0;
  };

  // Leaves this app entirely: the browser must reach the SSO host directly so its
  // session cookie and the Azure callback land on the same origin.
  const handleSsoLogin = () => {
    setLoading(true);
    startSsoLogin();
  };

  return (
    <div className="min-h-[100svh] bg-[#ECEEF3] p-5">
      <div className="mx-auto flex h-[calc(100svh-40px)] min-h-[720px] gap-8 rounded-[24px]">
        <div className="flex w-5/12 items-center justify-center">
          <div style={{ width: 550 }} className="w-full rounded-[7px] bg-white px-7 py-7">
            <div className="relative mb-8 flex w-full items-center">
              <img
                src={abgLogo}
                alt="ABG | accord business group"
                className="h-6 w-auto object-contain"
              />
              <span
                style={{ width: 250, left: 250 }}
                className="absolute -translate-x-1/2 text-[20px] font-bold text-[#6F7F96]"
              >
                Mynd
              </span>
            </div>

            <h1 className="mb-7 text-[20px] font-bold text-[#6F7F96]">
              AI That Thinks With You
            </h1>

            <form onSubmit={handleLogin}>
              <AuthInput
                label="Email address"
                type="email"
                placeholder="Enter your email"
                value={userName}
                onChange={setUserName}
                leftIcon={<Mail className="h-[18px] w-[18px]" />}
                isValid={isEmailValid}
                touched={emailTouched}
                setTouched={setEmailTouched}
                inputTextColorClassName="text-[#5b6f84]"
              />

              <AuthInput
                label="Password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={setPassword}
                leftIcon={<LockNoKeyholeIcon className="block" />}
                isValid={isPasswordValid}
                touched={passwordTouched}
                setTouched={setPasswordTouched}
                showPasswordToggle
              />

              <div className="mb-8 flex items-center justify-between">
                <label className="flex cursor-pointer items-center gap-2 text-[15px] font-normal text-[#3f5672]">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)}
                    className="h-[16px] w-[16px] cursor-pointer rounded-[10px] accent-[#2D2EFF]"
                  />
                  Remember me
                </label>

                <Link
                  to="/forgot-password"
                  className="text-[14px] font-normal text-[#6F7F96] hover:text-[#2D2EFF]"
                >
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="h-[40.7px] w-full rounded-[8px] bg-[#3538FF] text-[16px] font-semibold text-white transition hover:bg-[#2D2EFF] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? "Logging in..." : "Log In"}
              </button>

              {ssoEnabled && (
                <>
                  <div className="my-4 flex items-center gap-3">
                    <span className="h-px flex-1 bg-[#E3E7EE]" />
                    <span className="text-[13px] font-normal text-[#8391A2]">or</span>
                    <span className="h-px flex-1 bg-[#E3E7EE]" />
                  </div>

                  <button
                    type="button"
                    onClick={handleSsoLogin}
                    disabled={loading}
                    className="h-[40.7px] w-full rounded-[8px] border border-[#3538FF] bg-white text-[16px] font-semibold text-[#3538FF] transition hover:bg-[#F2F2FF] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Sign in with SSO
                  </button>
                </>
              )}
            </form>
          </div>
        </div>

        <OutterLeftPanel />
      </div>
    </div>
  );
}